import React, { useState, useEffect } from "react";
import api from "../../utils/axiosConfig";
import { useNavigate } from "react-router-dom";
import { FaTrashAlt, FaTimes, FaExclamationTriangle } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
export default function DeleteCart({ productId, taille, couleur, onDeleted }) {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
              await api.delete(`/api/shop/cart/remove`, {
        withCredentials: true,
        data: { productId, taille, couleur },
      });
      toast.success("Article supprimé du panier !", {
        onClick: () => navigate('/cart'),
        style: { cursor: 'pointer' }
      });
      onDeleted();
      setShowModal(false);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la suppression de l'article.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteClick = () => {
    setShowModal(true);
  };

  return (
    <>
      <motion.button
        onClick={handleDeleteClick}
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
        style={{
          padding: "10px 16px",
          backgroundColor: "rgba(220, 53, 69, 0.1)",
          backdropFilter: "blur(20px)",
          color: "#dc3545",
          border: "1px solid rgba(220, 53, 69, 0.3)",
          borderRadius: "12px",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "500",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          minWidth: "fit-content",
          fontFamily: "'Cormorant Garamond', serif",
          boxShadow: "0 4px 15px rgba(220, 53, 69, 0.1)",
          transition: "all 0.3s ease",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <FaTrashAlt style={{ fontSize: "14px" }} />
        Supprimer
      </motion.button>

      {/* Modal de confirmation */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
                          style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "100vw",
                height: "100vh",
                backdropFilter: "blur(0px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 99999,
                padding: "20px",
              }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 0.85, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "rgba(226, 225, 226, 0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(34, 139, 34, 0.3)",
                borderRadius: "20px",
                padding: "30px",
                maxWidth: "400px",
                width: "100%",
                textAlign: "center",
                boxShadow: "0 8px 32px rgba(34, 139, 34, 0.2)",
                position: "relative",
                overflow: "hidden",
                margin: "auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* Icône d'avertissement */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                style={{
                  width: "60px",
                  height: "60px",
                  background: "rgba(34, 139, 34, 0.2)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                  border: "1px solid rgba(34, 139, 34, 0.4)",
                }}
              >
                <FaExclamationTriangle style={{ fontSize: "24px", color: "#228b22" }} />
              </motion.div>

              {/* Titre */}
              <motion.h3
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "600",
                  color: "#2c1810",
                  marginBottom: "15px",
                  fontFamily: "'Cormorant Garamond', serif",
                }}
              >
                Confirmer la suppression
              </motion.h3>

              {/* Message */}
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                style={{
                  fontSize: "1rem",
                  color: "#5a4a3a",
                  marginBottom: "25px",
                  lineHeight: "1.5",
                  fontFamily: "'Cormorant Garamond', serif",
                }}
              >
                Voulez-vous vraiment supprimer cet article du panier ?
                <br />
                <span style={{ fontSize: "0.9rem", color: "#dc3545", fontWeight: "500" }}>
                  Cette action est irréversible.
                </span>
              </motion.p>

              {/* Boutons */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                style={{
                  display: "flex",
                  gap: "15px",
                  justifyContent: "center",
                }}
              >
                {/* Bouton Annuler */}
                <motion.button
                  onClick={() => setShowModal(false)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={isDeleting}
                  style={{
                    padding: "12px 24px",
                    background: "rgba(108, 117, 125, 0.1)",
                    backdropFilter: "blur(10px)",
                    color: "#6c757d",
                    border: "1px solid rgba(108, 117, 125, 0.3)",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    fontFamily: "'Cormorant Garamond', serif",
                    transition: "all 0.3s ease",
                    minWidth: "100px",
                  }}
                >
                  Annuler
                </motion.button>

                {/* Bouton Confirmer */}
                <motion.button
                  onClick={handleDelete}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={isDeleting}
                  style={{
                    padding: "12px 24px",
                    background: "rgba(34, 139, 34, 0.2)",
                    backdropFilter: "blur(10px)",
                    color: "#228b22",
                    border: "1px solid rgba(34, 139, 34, 0.4)",
                    borderRadius: "12px",
                    cursor: isDeleting ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    fontFamily: "'Cormorant Garamond', serif",
                    transition: "all 0.3s ease",
                    minWidth: "100px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  {isDeleting ? (
                    <>
                                             <div style={{
                         width: "16px",
                         height: "16px",
                         border: "2px solid rgba(34, 139, 34, 0.3)",
                         borderTop: "2px solid #228b22",
                         borderRadius: "50%",
                         animation: "spin 1s linear infinite",
                       }} />
                      Suppression...
                    </>
                  ) : (
                    <>
                      <FaTrashAlt style={{ fontSize: "14px" }} />
                      Supprimer
                    </>
                  )}
                </motion.button>
              </motion.div>

              {/* Bouton fermer */}
              <motion.button
                onClick={() => setShowModal(false)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                style={{
                  position: "absolute",
                  top: "15px",
                  right: "15px",
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "none",
                  borderRadius: "50%",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#5a4a3a",
                  fontSize: "14px",
                }}
              >
                <FaTimes />
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
